// frontend/src/pages/ProjectOpex.tsx
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fromDecimalToPercent, fromPercentToDecimal } from "@/utils/percent";
import { useProjectFormStore } from "@/store/project-form-provider";
import type { ProjectOpexRowUI } from "@/store/project-opex-slice";
import { ProjectSaveButton } from "@/components/project-save-button";
import { ProjectResetButton } from "@/components/project-reset-button";

type OpexUnit =
  | "EUR_PER_KWP"
  | "EUR_CT_PER_KWH"
  | "PERCENT_OF_SALES"
  | "EUR"
  | "PERCENT_OF_TOTAL_INVESTMENT";

const UNIT_OPTIONS: { value: OpexUnit; label: string }[] = [
  { value: "EUR_PER_KWP",                 label: "EUR/kWp p.a." },
  { value: "EUR_CT_PER_KWH",              label: "EUR ct./kWh" },
  { value: "PERCENT_OF_SALES",            label: "% of sales" },
  { value: "EUR",                         label: "EUR" },
  { value: "PERCENT_OF_TOTAL_INVESTMENT", label: "% of total investment" },
];

const isPercentUnit = (u: OpexUnit) =>
  u === "PERCENT_OF_SALES" || u === "PERCENT_OF_TOTAL_INVESTMENT";

const parseNum = (s: string): number => {
  if (s.trim() === "") return NaN;
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};


export default function ProjectOpex() {
  const {
    project,
    loading,
    error,
    resetAll,
    saveAll,
    savingAll,
    // OpEx slice from provider
    opexRows,
    setOpexRows,
    loadOpexItems,
    saveOpexItems,
    resetOpexItems,
    opexSaving,
    opexError,
    isOpexDirty,
  } = useProjectFormStore();

  const { t } = useTranslation();

  if (loading) return <div className="p-4 text-xs">{t("common.loading")}</div>;
  if (error) return <div className="p-4 text-xs text-elani-orange">{t("common.error_loading_data")}</div>;

  if (!opexRows) return <div className="p-4 text-xs">{t("common.loading")}</div>;

  // Local UI updates that write directly into provider state (keeps central store the source of truth)
  const updateRow = (idx: number, patch: Partial<ProjectOpexRowUI>) => {
    setOpexRows((prev) => {
      if (!prev) return prev;
      const copy = [...prev];
      const before = copy[idx];

      // If unit changes, adapt valueStr (% <-> decimal display)
      if (patch.unit && patch.unit !== before.unit) {
        const prevIsPct = isPercentUnit(before.unit as OpexUnit);
        const nextIsPct = isPercentUnit(patch.unit as OpexUnit);
        let nextValueStr = before.valueStr;
        const numeric = parseNum(before.valueStr);

        if (Number.isFinite(numeric)) {
          if (!prevIsPct && nextIsPct)       nextValueStr = String(fromDecimalToPercent(numeric));
          else if (prevIsPct && !nextIsPct)  nextValueStr = String(fromPercentToDecimal(numeric));
        } else {
          nextValueStr = "";
        }
        copy[idx] = { ...before, ...patch, valueStr: nextValueStr };
        return copy;
      }

      copy[idx] = { ...before, ...patch };
      return copy;
    });
  };

  const onAdd = () => {
    const nextPos = opexRows.length ? Math.max(...opexRows.map((r) => r.position ?? 0)) + 10 : 10;
    setOpexRows((prev) => (prev ?? []).concat([{
      id: 0,
      project_id: Number((project as any)?.project_id ?? 0),
      name: "",
      valueStr: "",
      unit: "EUR_PER_KWP",
      startStr: "",
      endStr: "",
      annualPercentStr: "",
      position: nextPos,
    }]));
  };

  const onDelete = (idx: number) => {
    setOpexRows((prev) => {
      if (!prev) return prev;
      const copy = [...prev];
      copy.splice(idx, 1);
      return copy;
    });
  };

  

  return (
    <div className="p-4 text-xs">
      <div className="mb-3 flex items-center justify-between">
        <button
          className="bg-gray-200 hover:bg-gray-300 rounded px-3 py-1"
          onClick={(e) => { e.preventDefault(); onAdd(); }}
          title="Add OpEx item"
          aria-label="Add OpEx item"
        >
          +
        </button>
      </div>

      {/* Content-sized table container */}
      <div className="inline-block rounded border">
        <table className="table-auto">
          <colgroup>
            <col style={{ width: 260 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 190 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 180 }} />
            <col style={{ width: 100 }} />
          </colgroup>

          <thead className="bg-gray-50">
            <tr className="text-left">
              <th className="p-2">{t("project.opex.columns.name")}</th>
              <th className="p-2">{t("project.opex.columns.value")}</th>
              <th className="p-2">{t("project.opex.columns.unit")}</th>
              <th className="p-2">{t("project.opex.columns.begin_year")}</th>
              <th className="p-2">{t("project.opex.columns.end_year")}</th>
              <th className="p-2">{t("project.opex.columns.annual_increase")}</th>
              <th className="p-2"></th>
            </tr>
          </thead>

          <tbody>
            {opexRows.map((r, i) => {
              const percentMode = isPercentUnit(r.unit);
              const showNamePlaceholder = !r.name?.trim();
              return (
                <tr key={r.id ? r.id : `new-${i}`} className="border-t">
                  <td className="p-2">
                    <input
                      className={`w-full border rounded px-2 py-1 ${showNamePlaceholder ? "italic text-gray-500" : ""}`}
                      value={r.name}
                      onChange={(e) => updateRow(i, { name: e.target.value })}
                      placeholder={t("project.opex.placeholders.unnamed")}
                    />
                  </td>

                  <td className="p-2">
                    <div className="relative">
                      <input
                        className={`w-full border rounded ${percentMode ? "pl-2 pr-6" : "px-2"} py-1`}
                        type="text"
                        inputMode="decimal"
                        value={r.valueStr}
                        onChange={(e) => updateRow(i, { valueStr: e.target.value })}
                        placeholder={percentMode ? t("project.opex.placeholders.example_percent") : t("project.opex.placeholders.example_decimal")}
                      />
                      {percentMode && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">%</span>
                      )}
                    </div>
                  </td>

                  <td className="p-2">
                    <select
                      className="w-full border rounded px-2 py-1"
                      value={r.unit}
                      onChange={(e) => updateRow(i, { unit: e.target.value as OpexUnit })}
                    >
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="p-2">
                    <input
                      className="w-full border rounded px-2 py-1"
                      type="text"
                      inputMode="numeric"
                      value={r.startStr}
                      onChange={(e) => updateRow(i, { startStr: e.target.value })}
                      placeholder={t("project.opex.placeholders.sop")}
                    />
                  </td>

                  <td className="p-2">
                    <input
                      className="w-full border rounded px-2 py-1"
                      type="text"
                      inputMode="numeric"
                      value={r.endStr}
                      onChange={(e) => updateRow(i, { endStr: e.target.value })}
                      placeholder={t("project.opex.placeholders.eop")}
                    />
                  </td>

                  <td className="p-2">
                    <div className="relative">
                      <input
                        className="w-full border rounded pl-2 pr-6 py-1"
                        type="text"
                        inputMode="decimal"
                        value={r.annualPercentStr}
                        onChange={(e) => updateRow(i, { annualPercentStr: e.target.value })}
                        placeholder=""
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">%</span>
                    </div>
                  </td>

                  <td className="p-2">
                    <button
                      className="bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                      onClick={(e) => { e.preventDefault(); onDelete(i); }}
                    >
                      {t("actions.delete")}
                    </button>
                  </td>
                </tr>
              );
            })}

            {opexRows.length === 0 && (
              <tr className="border-t">
                <td colSpan={7} className="p-3 italic text-gray-500">
                  {t("project.opex.no_items")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="py-2 text-[11px] text-gray-500">
        {t("project.opex.hint")}
      </div>

      {(opexError) && (
        <div className="mt-2 text-xs text-elani-orange">{opexError}</div>
      )}

      <div className="pt-3 flex gap-2">
        <ProjectSaveButton onSave={saveAll} saving={savingAll} />
        <ProjectResetButton onReset={resetAll} saving={savingAll} />
      </div>
    </div>
  );
}